import PropTypes from 'prop-types';
// components
import Label from 'src/components/Label';
import Iconify from 'src/components/Iconify';
// utils
import { parseApiDate } from 'src/utils/smartReminderRules';

// Shared payment-link delivery chip logic — used by BookedPaymentStatusCard
// (Overview tab) and PaymentCard (Payments tab) so both surfaces render
// identical channel/state derivation and chip visuals. See
// /Users/admin/Desktop/2026-09-23-payment-link-delivery-waves.md contract E.

export const DELIVERY_STATE_COLOR = {
    not_requested: 'default',
    blocked: 'error',
    failed: 'error',
    queued: 'info',
    accepted: 'info',
    sent: 'success',
    delivered: 'success',
    read: 'success',
};

export const CHANNEL_LABEL = { email: 'Email', whatsapp: 'WhatsApp' };

export const CHANNEL_ICON = { email: 'eva:email-fill', whatsapp: 'mdi:whatsapp' };

// Contract E documents `deliveries` as newest-first, but real data isn't
// reliably sorted (e.g. resends can land duplicate/near-tied timestamps) —
// so derive "newest" explicitly via parseApiDate rather than trusting array
// order. Groups ALL deliveries by channel and keeps, per channel, the one
// with the latest parsed created_at (a channel's own superseded failure is
// dropped in favour of its later success).
export function newestDeliveriesByChannel(deliveries) {
    if (!Array.isArray(deliveries)) return {};
    const byChannel = {};
    deliveries.forEach((d) => {
        const channel = String(d?.channel).toLowerCase();
        if (!channel) return;
        const existing = byChannel[channel];
        if (!existing) {
            byChannel[channel] = d;
            return;
        }
        const existingTime = parseApiDate(existing.created_at)?.valueOf() || 0;
        const candidateTime = parseApiDate(d.created_at)?.valueOf() || 0;
        if (candidateTime >= existingTime) byChannel[channel] = d;
    });
    return byChannel;
}

// The single most-recent delivery across ALL channels (for the "Sent via …
// · {relative time}" line) — same explicit-parse approach as above.
export function newestDeliveryOverall(deliveries) {
    if (!Array.isArray(deliveries) || deliveries.length === 0) return null;
    return deliveries.reduce((best, d) => {
        if (!best) return d;
        const bestTime = parseApiDate(best.created_at)?.valueOf() || 0;
        const time = parseApiDate(d.created_at)?.valueOf() || 0;
        return time >= bestTime ? d : best;
    }, null);
}

// Ordered list of channels ['email', 'whatsapp'] that have at least one
// delivery, in the fixed display order.
export function orderedChannelsWithDeliveries(deliveries) {
    const byChannel = newestDeliveriesByChannel(deliveries);
    return ['email', 'whatsapp'].filter((c) => byChannel[c]);
}

// A single channel's chip — same visuals + text ("Email sent" / "WhatsApp
// read") everywhere it's used.
export function DeliveryChip({ channel, delivery }) {
    if (!delivery) return null;
    return (
        <Label
            color={DELIVERY_STATE_COLOR[delivery.state] || 'default'}
            variant="ghost"
            startIcon={<Iconify icon={CHANNEL_ICON[channel] || 'eva:email-fill'} width={12} />}
        >
            {CHANNEL_LABEL[channel] || channel} {delivery.state}
        </Label>
    );
}
DeliveryChip.propTypes = {
    channel: PropTypes.oneOf(['email', 'whatsapp']).isRequired,
    delivery: PropTypes.object,
};
DeliveryChip.defaultProps = { delivery: null };

// A full row of chips, one per channel with deliveries — derives channels +
// newest-per-channel itself so callers just pass the raw `deliveries` array.
export function DeliveryChipRow({ deliveries }) {
    const byChannel = newestDeliveriesByChannel(deliveries);
    const channels = ['email', 'whatsapp'].filter((c) => byChannel[c]);
    if (channels.length === 0) return null;
    return channels.map((channel) => (
        <DeliveryChip key={channel} channel={channel} delivery={byChannel[channel]} />
    ));
}
DeliveryChipRow.propTypes = {
    deliveries: PropTypes.array,
};
DeliveryChipRow.defaultProps = { deliveries: [] };
